import * as _ from 'lodash';
import { BigNumber } from 'bignumber.js';
import { TromanError } from "../../../common/TromanError";
import { ActiveWageType, PaymentsCalculation } from "./PaymentsCalculation";
import { claimTypes } from '../../objects/Claim';
import { wageModels } from '../../objects/WageType';
import { lostIncomeBenefitsMode } from '../../objects/PolitikerSettings';

/**
 * @typedef { import('../../objects/Attendance').Attendance } Attendance
 * @typedef { import('../../objects/Claim').Claim } Claim
 * @typedef { import('../../objects/Claim').ClaimNugget } ClaimNugget
 * @typedef { import('../../objects/Claim').TravelNugget } TravelNugget
 * @typedef { import('../../objects/Claim').ParkingNugget } ParkingNugget
 * @typedef { import('../../objects/Claim').CongestionNugget } CongestionNugget
 * @typedef { import('../../objects/Claim').PublicTransportNugget } PublicTransportNugget
 * @typedef { import('../../objects/Claim').TravelTimeNugget } TravelTimeNugget
 * @typedef { import('../../objects/Claim').ReceiptExpenseNugget } ReceiptExpenseNugget
 * @typedef { import('../../objects/Claim').AdjusterNugget } AdjusterNugget
 * @typedef { import('../../objects/Claim').LostIncomeAmountNugget } LostIncomeAmountNugget
 * @typedef { import('../../objects/Claim').LostIncomeNumberNugget } LostIncomeNumberNugget
 * @typedef { import('../../objects/Claim').LostIncomeRateNugget } LostIncomeRateNugget
 * @typedef { import('../../objects/PolitikerSettings').PolitikerSettings } PolitikerSettings
 * @typedef { import('../EmployerOptions').EmployerOptions } EmployerOptions
 */

/**
 * 
 * @param {Number|string} amount
 * @param {Number|string} percent
 */
function calculateAmountPercentDP2(amount, percent) {
    const a = new BigNumber(amount);
    const p = new BigNumber(percent);
    return a.times(p.dividedBy(100)).dp(2).toNumber();
}

/**
 * 
 * @param {Number} value
 */
function numberFromMinutesDP2(value) {
    return new BigNumber(value).dividedBy(60).dp(2).toNumber();
}

export class PolitikerCalculator {
    /**
     * @param {Date} date
     * @param {PolitikerSettings} settings
     */
    constructor(date, settings) {

        this.settings = settings;

        // TODO only try to get period if the wage type is actually used.
        this.awtVat = ActiveWageType.forDate(settings.Vat.WageType, date);
        this.awtLostIncomeAmount = ActiveWageType.forDate(settings.LostIncomeAmount.WageType, date);
        this.awtLostIncomeNumberUnit = ActiveWageType.forDate(settings.LostIncomeNumberUnit.WageType, date);
        this.awtLostIncomeNumber = ActiveWageType.forDate(settings.LostIncomeNumber.WageType, date);
        this.awtRetirementBenefits = ActiveWageType.forDate(settings.RetirementBenefits.WageType, date);
        this.awtVacationBenefits = ActiveWageType.forDate(settings.VacationBenefits.WageType, date);
        this.awtTravel = ActiveWageType.forDate(settings.Travel.WageType, date);
        this.awtParking = ActiveWageType.forDate(settings.Parking.WageType, date);
        this.awtCongestion = ActiveWageType.forDate(settings.Congestion.WageType, date);
        this.awtPublicTransport = ActiveWageType.forDate(settings.PublicTransport.WageType, date);
        this.awtTravelTime = ActiveWageType.forDate(settings.TravelTime.WageType, date);
        this.awtExpenses = ActiveWageType.forDate(settings.Expenses.WageType, date);
        this.awtAdjuster = ActiveWageType.forDate(settings.Adjuster.WageType, date);
    }

    /**
     * @param {Date} date
     * @param {PolitikerSettings[]} settingsList
     */
    static forDate(date, settingsList) {
        let inorder = _.orderBy(settingsList, x => x.StartDate, ['desc']);
        let settings = _.find(inorder, x => x.StartDate <= date);

        if (settings == null) {
            return null;
        } else {
            return new PolitikerCalculator(date, settings);
        }
    }

    /**
     * @param {Attendance} attendance
     * @param {Claim} claim
     * @param {EmployerOptions?} selectedEmployer
     */
    calculateClaim(attendance, claim, selectedEmployer) {
        let calculation = new PolitikerCalculation(this, attendance);
        calculation.selectedEmployer = selectedEmployer || null;
        calculation.calculateClaim(claim);
        return calculation.result;
    }

    /**
     * @param {Claim} claim
     */
    isEmployerDependent(claim) {
        return claim.ClaimType === claimTypes.lostIncomeNumber
            && this.awtLostIncomeNumber
            && this.awtLostIncomeNumber.wageModel.id === wageModels.lostIncomeIndividual;
    }
}

export class PolitikerCalculation extends PaymentsCalculation {
    /**
     * @param {PolitikerCalculator} calculator
     * @param {Attendance} attendance
     */
    constructor(calculator, attendance) {
        super(attendance);
        this.calculator = calculator;
        /** @type {EmployerOptions?} */
        this.selectedEmployer = null;
    }

    /**
     * @param {Claim} claim
     */
    calculateClaim(claim) {
        switch (claim.ClaimType) {
            case claimTypes.lostIncomeAmount:
                this.calculateLostIncomeAmount(claim.Nugget);
                break;
            case claimTypes.lostIncomeNumber:
                this.calculateLostIncomeNumber(claim.Nugget);
                break;
            case claimTypes.lostIncomeRate:
                this.calculateLostIncomeRate(claim.Nugget);
                break;
            case claimTypes.legacyTravel:
                this.calculateLegacyTravel(claim.Nugget);
                break;
            case claimTypes.travel:
                this.calculateTravel(claim.Nugget);
                break;
            case claimTypes.parking:
                this.calculateParking(claim.Nugget);
                break;
            case claimTypes.congestion:
                this.calculateCongestion(claim.Nugget);
                break;
            case claimTypes.publicTransport:
                this.calculatePublicTransport(claim.Nugget);
                break;
            case claimTypes.travelTime:
                this.calculateTravelTime(claim.Nugget);
                break;
            case claimTypes.expense:
                this.calculateExpense(claim.Nugget);
                break;
            case claimTypes.adjuster:
                this.calculateAdjuster(claim.Nugget);
                break;
            default:
                throw new TromanError('unknown_claim_type', 'Anspråkstypen stöds ej');
        }
    }

    /**
     * @param {number} amount
     */
    tryAddVat(amount) {
        if (amount && amount > 0) {
            if (this.calculator.awtVat == null) {
                throw new TromanError('missing_vat_wagetype', 'Det finns ingen konfigurerad löneart för moms');
            }
            this.addAmountPayment(this.calculator.awtVat, amount);
        }
    }

    /**
     * @param {ClaimNugget} nugget
     * @param {number} lostIncomeAmount
     */
    calculateLostIncomeBenefits(nugget, lostIncomeAmount) {
        const retirementMode = this.calculator.settings.RetirementBenefits.Mode;
        if (retirementMode === lostIncomeBenefitsMode.always || (retirementMode === lostIncomeBenefitsMode.byrequest && nugget.RequestRetirementBenefits)) {

            if (this.calculator.awtRetirementBenefits == null) {
                throw new TromanError('missing_retirementbenefits_wagetype', 'Det finns ingen konfigurerad löneart för pensionsförmån');
            }

            this.addAmountPayment(this.calculator.awtRetirementBenefits, calculateAmountPercentDP2(lostIncomeAmount, this.calculator.settings.RetirementBenefits.Percent));
        }

        const vacationMode = this.calculator.settings.VacationBenefits.Mode;
        if (vacationMode === lostIncomeBenefitsMode.always || (vacationMode === lostIncomeBenefitsMode.byrequest && nugget.RequestVacationBenefits)) {

            if (this.calculator.awtVacationBenefits == null) {
                throw new TromanError('missing_vacationbenefits_wagetype', 'Det finns ingen konfigurerad löneart för semesterersättning');
            }

            this.addAmountPayment(this.calculator.awtVacationBenefits, calculateAmountPercentDP2(lostIncomeAmount, this.calculator.settings.VacationBenefits.Percent));
        }
    }

    /**
     * @param {LostIncomeRateNugget} nugget
     */
    calculateLostIncomeRate(nugget) {
        if (this.calculator.awtLostIncomeNumberUnit == null) {
            throw new TromanError('missing_lostincome_numberunit_wagetype', 'Det finns ingen konfigurerad löneart för förlorad inkomst (antal & á-pris)');
        }
        let cp = this.addNumberUnitPayment(this.calculator.awtLostIncomeNumberUnit, nugget.Hours, nugget.HourlyRate);
        this.calculateLostIncomeBenefits(nugget, cp.payment.Amount);
    }
    
    /**
     * @param {LostIncomeAmountNugget} nugget
     */
    calculateLostIncomeAmount(nugget) {
        if (this.calculator.awtLostIncomeAmount == null) {
            throw new TromanError('missing_lostincome_amount_wagetype', 'Det finns ingen konfigurerad löneart för förlorad inkomst (belopp)');
        }
        let cp = this.addAmountPayment(this.calculator.awtLostIncomeAmount, nugget.Amount);
        this.calculateLostIncomeBenefits(nugget, cp.payment.Amount);
    }
    
    /**
     * @param {LostIncomeNumberNugget} nugget
     */
    calculateLostIncomeNumber(nugget) {
        const awt = this.calculator.awtLostIncomeNumber;

        if (awt == null) {
            throw new TromanError('missing_lostincome_number_wagetype', 'Det finns ingen konfigurerad löneart för förlorad inkomst (antal)');
        }

        if (awt.wageModel.id == wageModels.lostIncomeIndividual) {
            if (this.selectedEmployer) {
                let cp = this.addNumberUnitPayment(awt, nugget.Hours, this.selectedEmployer.earning.UnitPrice);
                cp.chosenEmployer = this.selectedEmployer.name;
                this.calculateLostIncomeBenefits(nugget, cp.payment.Amount);
            }
        } else {
            this.addNumberPayment(awt, nugget.Hours);
        }
    }

    /**
     * @param {LegacyTravelExpenseNugget} nugget
     */
    calculateLegacyTravel(nugget) {
        if (nugget.DrivingDistance && nugget.DrivingDistance > 0) {
            if (this.calculator.awtTravel == null) {
                throw new TromanError('missing_travel_wagetype', 'Det finns ingen konfigurerad löneart för km-ersättning');
            }
            this.addNumberPayment(this.calculator.awtTravel, nugget.DrivingDistance);
        }

        if (nugget.Parking && nugget.Parking > 0) {
            if (this.calculator.awtParking == null) {
                throw new TromanError('missing_parking_wagetype', 'Det finns ingen konfigurerad löneart för parkering');
            }
            this.addAmountPayment(this.calculator.awtParking, nugget.Parking);

            this.tryAddVat(nugget.ParkingVat);
        }

        if (nugget.Congestion && nugget.Congestion > 0) {
            if (this.calculator.awtCongestion == null) {
                throw new TromanError('missing_congestion_wagetype', 'Det finns ingen konfigurerad löneart för trängselskatt');
            }
            this.addAmountPayment(this.calculator.awtCongestion, nugget.Congestion);
        }

        if (nugget.PublicTransport && nugget.PublicTransport > 0) {
            if (this.calculator.awtPublicTransport == null) {
                throw new TromanError('missing_publictransport_wagetype', 'Det finns ingen konfigurerad löneart för kollektivtrafik');
            }
            this.addAmountPayment(this.calculator.awtPublicTransport, nugget.PublicTransport);

            this.tryAddVat(nugget.PublicTransportVat);
        }

        if (nugget.TravelTime && nugget.TravelTime > 0) {
            if (this.calculator.awtTravelTime == null) {
                throw new TromanError('missing_traveltime_wagetype', 'Det finns ingen konfigurerad löneart för restid');
            }
            let number = numberFromMinutesDP2(nugget.TravelTime);
            this.addNumberPayment(this.calculator.awtTravelTime, number);
        }
    }
    
    /**
     * @param {TravelNugget} nugget
     */
    calculateTravel(nugget) {
        if (this.calculator.awtTravel == null) {
            throw new TromanError('missing_travel_wagetype', 'Det finns ingen konfigurerad löneart för km-ersättning');
        }
        this.addNumberPayment(this.calculator.awtTravel, nugget.DrivingDistance);
    }

    /**
     * @param {ParkingNugget} nugget
     */
    calculateParking(nugget) {
        if (this.calculator.awtParking == null) {
            throw new TromanError('missing_parking_wagetype', 'Det finns ingen konfigurerad löneart för parkering');
        }
        this.addAmountPayment(this.calculator.awtParking, nugget.Parking);

        this.tryAddVat(nugget.ParkingVat);
    }

    /**
     * @param {CongestionNugget} nugget
     */
    calculateCongestion(nugget) {
        if (this.calculator.awtCongestion == null) {
            throw new TromanError('missing_congestion_wagetype', 'Det finns ingen konfigurerad löneart för trängselskatt');
        }
        this.addAmountPayment(this.calculator.awtCongestion, nugget.Congestion);
    }

    /**
     * @param {PublicTransportNugget} nugget
     */
    calculatePublicTransport(nugget) {
        if (this.calculator.awtPublicTransport == null) {
            throw new TromanError('missing_publictransport_wagetype', 'Det finns ingen konfigurerad löneart för kollektivtrafik');
        }
        this.addAmountPayment(this.calculator.awtPublicTransport, nugget.PublicTransport);

        this.tryAddVat(nugget.PublicTransportVat);
    }

    /**
     * @param {TravelTimeNugget} nugget
     */
    calculateTravelTime(nugget) {
        if (this.calculator.awtTravelTime == null) {
            throw new TromanError('missing_traveltime_wagetype', 'Det finns ingen konfigurerad löneart för restid');
        }
        let number = numberFromMinutesDP2(nugget.TravelTime);
        this.addNumberPayment(this.calculator.awtTravelTime, number);
    }

    /**
     * @param {ReceiptExpenseNugget} nugget
     */
    calculateExpense(nugget) {
        if (this.calculator.awtExpenses == null) {
            throw new TromanError('missing_expenses_wagetype', 'Det finns ingen konfigurerad löneart för utlägg');
        }
        this.addAmountPayment(this.calculator.awtExpenses, nugget.Amount);

        this.tryAddVat(nugget.Vat);
    }

    /**
     */
    calculateAdjuster() {
        if (this.calculator.awtAdjuster == null) {
            throw new TromanError('missing_adjuster_wagetype', 'Det finns ingen konfigurerad löneart för justeringsarvode');
        }
        this.addNumberPayment(this.calculator.awtAdjuster, 1);
    }
}
